'use client';
import React from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import NextLink from '@src/components/Shared/NextLink';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import VelocitySetInput, { VELOCITY_SET_GRID } from './VelocitySetInput';

import {
  EstimatedOneRepMaxCardPropTypes,
  LoadVelocitySet,
  MovementTypes,
} from './EstimatedOneRepMaxCard.types';
import {
  convertLoad,
  estimateOneRepMax,
  POUNDS_PER_KILOGRAM,
} from './EstimatedOneRepMaxCard.utils';

const units: UnitTypes[] = [
  { id: 'kilograms', name: 'Kilograms', unit: 'kg' },
  { id: 'pounds', name: 'Pounds', unit: 'lb' },
];

// Velocity at 1RM per movement, prefilled into the velocity field and editable from there.
const movements: MovementTypes[] = [
  { id: 'squat', name: 'Squat', unit: 'm/s', velocityAtOneRepMax: 0.27 },
  { id: 'bench-press', name: 'Bench Press', unit: 'm/s', velocityAtOneRepMax: 0.15 },
  { id: 'deadlift', name: 'Deadlift', unit: 'm/s', velocityAtOneRepMax: 0.33 },
];

const SET_COUNT = 3;

const DEFAULT_VELOCITY_AT_ONE_REP_MAX = movements[0].velocityAtOneRepMax.toFixed(2);

const initialSets: LoadVelocitySet[] = Array.from({ length: SET_COUNT }, () => ({
  load: '',
  velocity: '',
}));

const EstimatedOneRepMaxCard: React.FC<EstimatedOneRepMaxCardPropTypes> = () => {
  const isMobile = useIsMobile();

  const [movementId, setMovementId] = React.useState(movements[0].id);
  const [unitId, setUnitId] = React.useState(units[0].id);

  // Live inputs — used directly on desktop
  const [sets, setSets] = React.useState<LoadVelocitySet[]>(initialSets);
  const [velocityInput, setVelocityInput] = React.useState(DEFAULT_VELOCITY_AT_ONE_REP_MAX);

  // Committed inputs — what mobile calculates from, once Calculate is pressed
  const [committedSets, setCommittedSets] = React.useState<LoadVelocitySet[]>(initialSets);
  const [committedVelocity, setCommittedVelocity] = React.useState(DEFAULT_VELOCITY_AT_ONE_REP_MAX);

  const handleCalculate = () => {
    setCommittedSets(sets);
    setCommittedVelocity(velocityInput);
  };

  const activeSets = isMobile ? committedSets : sets;
  const activeVelocity = isMobile ? committedVelocity : velocityInput;
  const hasPendingChanges =
    isMobile && (sets !== committedSets || velocityInput !== committedVelocity);

  const activeUnit = units.find(({ id }) => id === unitId) ?? units[0];

  // Picking a movement prefills its velocity at 1RM, which the user can still overwrite.
  const handleMovementChange = (nextMovementId: string) => {
    setMovementId(nextMovementId);

    const movement = movements.find(({ id }) => id === nextMovementId);
    if (movement) setVelocityInput(movement.velocityAtOneRepMax.toFixed(2));
  };

  // Switching units rewrites the loads already entered, so the profile is not lost.
  const handleUnitChange = (nextUnitId: string) => {
    if (nextUnitId === unitId) return;

    const factor = nextUnitId === 'pounds' ? POUNDS_PER_KILOGRAM : 1 / POUNDS_PER_KILOGRAM;
    const convert = (list: LoadVelocitySet[]) =>
      list.map((set) => ({ ...set, load: convertLoad(set.load, factor) }));

    const nextSets = convert(sets);

    // Keep the two lists identical when nothing is pending, so no phantom recalculation appears.
    setCommittedSets(committedSets === sets ? nextSets : convert(committedSets));
    setSets(nextSets);
    setUnitId(nextUnitId);
  };

  const handleSetChange = (index: number, field: keyof LoadVelocitySet, value: string) =>
    setSets((previous) =>
      previous.map((set, setIndex) => (setIndex === index ? { ...set, [field]: value } : set))
    );

  // --- calculation ---
  const oneRepMax = estimateOneRepMax(activeSets, parseFloat(activeVelocity));
  const formattedOneRepMax =
    oneRepMax === null
      ? '0.0'
      : oneRepMax.toLocaleString('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });

  // --- Debounce the result by 3000 ms, then report the calculation ---
  const debouncedOneRepMax = useDebounce(formattedOneRepMax, 3000);

  React.useEffect(() => {
    if (
      oneRepMax === null ||
      isNaN(Number(debouncedOneRepMax)) ||
      !isFinite(Number(debouncedOneRepMax))
    ) {
      return;
    }

    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'msk_calculator_calculation_performed',
        eventCategory: 'msk_calculator_usage',
        eventLabel: 'msk_calculator_estimated_1rm',
        value: 1,
      });
    }
    // Only re-run when the debounced result changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOneRepMax]);

  return (
    <CardMetric
      id='calculator-estimated-1rm'
      title='Estimated 1RM'
      productIcons={[
        {
          alt: 'GymAware App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/5mc1s0ce2l4PPHNq9ogjGq/3c4f909bbef18251eb513a02783e1727/gymaware-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2rem] text-[1.2rem]'>
        The <strong className='font-medium'>estimated 1RM</strong> uses the relationship between
        load and bar speed to predict a one-rep max from submaximal sets, without a maximal attempt.
      </p>

      {/* — Movement and unit selectors — */}
      <div className='mb-[2.4rem] flex flex-col gap-[1.6rem] sm:flex-row'>
        <UnitDropdown
          className='mb-[0]! [&>div>button]:min-w-[9rem]'
          label='Movement:'
          units={movements}
          activeUnitId={movementId}
          onChange={handleMovementChange}
        />
        <UnitDropdown
          className='mb-[0]! [&>div>button]:min-w-[9rem]'
          label='Unit of measurement:'
          units={units}
          activeUnitId={unitId}
          onChange={handleUnitChange}
        />
      </div>

      {/* — Velocity at 1RM — */}
      <ForceInput
        className='mb-[1.3rem]'
        labelLines={['Velocity at 1RM', 'Typical for this movement']}
        placeholder='m/s'
        activeUnit='m/s'
        value={velocityInput}
        onChange={(e) => setVelocityInput(e.target.value)}
      />
      <p className='mb-[2.4rem] text-[1rem] text-pretty text-[#6B6B6B] sm:text-[1.2rem]'>
        Population average for this lift. Adjust if you have measured this athlete&rsquo;s value.
      </p>

      {/* — Sets — */}
      <div
        className={`${VELOCITY_SET_GRID} mb-[0.8rem] px-[1rem] text-[1rem] font-medium sm:px-[2rem] sm:text-[1.2rem]`}
      >
        <span />
        <span className='pl-[1rem] sm:pl-[1.6rem]'>Load ({activeUnit.unit})</span>
        <span className='pl-[1rem] sm:pl-[1.6rem]'>Mean velocity (m/s)</span>
      </div>

      {sets.map((set, index) => (
        <VelocitySetInput
          key={index}
          className='mb-[1rem]'
          index={index + 1}
          loadLabel={activeUnit.name.toLowerCase()}
          load={set.load}
          velocity={set.velocity}
          onLoadChange={(e) => handleSetChange(index, 'load', e.target.value)}
          onVelocityChange={(e) => handleSetChange(index, 'velocity', e.target.value)}
        />
      ))}

      {/* — Result — */}
      <div className='text-primary mt-[2.4rem] mb-[3.1rem] flex items-center justify-between font-normal'>
        <span className='ml-auto text-[1.4rem] font-medium'>Estimated 1RM</span>
        <span className='ml-[2rem] border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {formattedOneRepMax}
          <span className='text-[1.6rem] font-light'>{activeUnit.unit}</span>
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='text-primary relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 md:block' />

          <p className='text-pretty'>
            <NextLink
              className='font-light underline'
              href='https://valdperformance.com/news/velocity-based-training-101'
            >
              Learn more
            </NextLink>{' '}
            about velocity-based training and how GymAware builds load-velocity profiles.
          </p>
        </div>

        {isMobile && (
          <button
            onClick={handleCalculate}
            disabled={!hasPendingChanges}
            className='common-btn-transition border-primary bg-primary hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2 relative right-0 bottom-0 ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] px-[2rem] py-[0.8rem] text-[1.6rem] leading-[1.6rem] font-normal tracking-[-0.0304rem] text-[#fff]!'
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default EstimatedOneRepMaxCard;
